import produce from 'immer';
import { call, put, select, take, takeEvery } from 'typed-redux-saga/macro';

import { debugPrivacy as debug } from 'Lib/debug';
import { getRehydrateLifecycle } from 'Lib/persist';
import { GetActionByType } from 'Lib/reduxType';
import { processResponse } from 'Lib/reduxUtils';
import { withErrorHandling } from 'Lib/sagaHelpers';
import eventEmitter, { PRIVACY_SETTINGS_CONFIRMED } from 'Lib/utils/emitter';
import { getServiceFn } from 'Services';
import { CMP_SERVICE } from 'Services/constants';

import { setChatSuggestionConsentsSuccess } from 'Store/appSettings/actions';
import { SET_CHAT_SUGGESTIONS_CONSENTS } from 'Store/appSettings/constants';
import { getChatSuggestionConsents } from 'Store/appSettings/selectors';
import { ChatSuggestionConsents } from 'Store/appSettings/types';
import { STORE_PRIVACY } from 'Store/constants';
import { initialAuthCheckComplete, privacyPreferencesSet } from 'Store/lifecycles';
import { fetchMeta } from 'Store/meta/actions';
import { FETCH_META_SUCCESS } from 'Store/meta/constants';
import { LOGIN_SUCCESS } from 'Store/mfa/constants';
import { closeModal, openModal } from 'Store/modals/actions';
import { MODAL_MESSAGING_CONSENT, MODAL_PRIVACY_PREFERENCES } from 'Store/modals/constants';
import { selectCurrentModal } from 'Store/modals/selectors';
import { getIsUserLoggedIn } from 'Store/user/selectors';

import {
  CHAT_SUGGESTION_CONSENT_ACCEPTED,
  CHAT_SUGGESTION_CONSENT_NOT_ACCEPTED,
  CONSENT_ACTION_ACCEPT,
} from 'Containers/ChatSuggestionsConsent/constants';

import {
  fetchConsents,
  fetchConsentsError,
  fetchConsentsRequest,
  fetchConsentsSuccess,
  privacySettingsPrompt,
  updateBiometricConsentsError,
  updateBiometricConsentsSuccess,
  updateConsents,
  updateConsentsError,
  updateConsentsRequest,
  updateConsentsSuccess,
  updateMessagingConsentsSuccess,
} from './actions';
import {
  FETCH_CONSENTS,
  FETCH_CONSENTS_ERROR,
  FETCH_CONSENTS_SUCCESS,
  PRIVACY_SETTINGS_MANAGE,
  UPDATE_BIOMETRIC_CONSENTS,
  UPDATE_BIOMETRIC_CONSENTS_SUCCESS,
  UPDATE_CONSENTS,
  UPDATE_CONSENTS_ERROR,
  UPDATE_CONSENTS_SUCCESS,
  UPDATE_MESSAGING_CONSENTS,
  UPDATE_MESSAGING_CONSENTS_SUCCESS,
} from './constants';
import { consentsErrorResponseSchema, consentsResponseSchema } from './schemas';
import {
  getHasDeferredUpdate,
  getNeedsPrivacyPrompt,
  getPrivacyCategoryData,
  getPrivacyConsentData,
  getPrivacyConsents,
  getPrivacyUserConsents,
} from './selectors';
import type { PrivacyCategoryData, PrivacyConsentData } from './types';

type ConsentRequestBodyCategory = {
  code: string;
  default_value: boolean;
  enabled: boolean;
  responded: boolean;
  requires_input: boolean;
};

type ConsentRequestBodyConsent = {
  category_code: string;
  code: string;
  enabled: boolean;
  responded: boolean;
  requires_input: boolean;
};

type ConsentRequestBody = {
  categories: ConsentRequestBodyCategory[];
  consents: ConsentRequestBodyConsent[];
};

function* formatConsentRequestBody(explicitConsents?: Record<string, boolean>) {
  // Use the current consents if they are not passed in
  let userConsents: Record<string, boolean>;
  if (explicitConsents) {
    userConsents = explicitConsents;
  } else {
    userConsents = yield* select(getPrivacyUserConsents);
  }

  const categoryData: {
    [key: string]: PrivacyCategoryData;
  } = yield* select(getPrivacyCategoryData);
  const consentData: {
    [key: string]: PrivacyConsentData;
  } = yield* select(getPrivacyConsentData);

  const categories: ConsentRequestBodyCategory[] = (Object.entries(categoryData) as any[])
    .filter(
      ([, category]: [string, PrivacyCategoryData]) =>
        category.changeable && userConsents[`:${category.code}`] != null
    )
    .map(([, category]: [string, PrivacyCategoryData]) => ({
      code: category.code,
      default_value: userConsents[`:${category.code}`],
      enabled: category.consents.reduce(
        (anyEnabled, code) => anyEnabled || userConsents[code],
        false
      ),
      responded: userConsents[`:${category.code}`] != null,
      requires_input: category.requiresInput,
    }));

  const consents: ConsentRequestBodyConsent[] = (Object.entries(consentData) as any[])
    .filter(
      ([, consent]: [string, PrivacyConsentData]) =>
        consent.changeable && userConsents[consent.code] != null
    )
    .map(([, consent]: [string, PrivacyConsentData]) => ({
      category_code: consent.categoryCode,
      code: consent.code,
      enabled: userConsents[consent.code],
      responded: userConsents[consent.code] != null,
      requires_input: consent.requiresInput,
    }));

  return { categories, consents };
}

const updateConsentsSaga = withErrorHandling(function* _updateConsentsSaga(
  action: GetActionByType<typeof UPDATE_CONSENTS>
) {
  // This will apply any default consents
  const prevConsents = yield* select(getPrivacyConsents);

  // Determine consents payload
  const { consents: payloadConsent = {} } = action.payload;
  let nextConsents;
  if (typeof payloadConsent === 'boolean') {
    nextConsents = {};
    Object.keys(prevConsents).forEach((key) => {
      nextConsents[key] = payloadConsent;
    });
  } else {
    nextConsents = { ...prevConsents, ...payloadConsent };
  }
  const consentRequestBody: ConsentRequestBody = yield* call(
    formatConsentRequestBody,
    nextConsents
  );

  const isAuthed = yield* select(getIsUserLoggedIn);
  if (isAuthed) {
    yield* put(updateConsentsRequest());
    try {
      const response = yield* call(
        yield* call(getServiceFn, CMP_SERVICE, 'updateUserConsents' as const),
        consentRequestBody
      );
      const payload = yield processResponse(response, consentsResponseSchema);
      yield* put(updateConsentsSuccess(payload, false));
    } catch (err: any) {
      // @TODO: Handle error path more explicitly
      const { error: response } = err;
      const payload = yield processResponse(response, consentsErrorResponseSchema);
      yield* put(updateConsentsError(payload));
    }
  } else {
    // For pre-auth users, save locally and send to server after login
    const payload = { result: { consents: nextConsents } };
    yield* put(updateConsentsSuccess(payload, true));
  }

  // Emit event for tracker initialization
  const updatedConsents = yield* select(getPrivacyConsents);
  eventEmitter.emit(PRIVACY_SETTINGS_CONFIRMED, updatedConsents);

  // Dismiss the privacy preferences modal if it is open
  const currentModal = yield* select(selectCurrentModal);
  if (currentModal?.type === MODAL_PRIVACY_PREFERENCES) {
    yield* put(closeModal());
  }
});

export const setChatSuggestionsConsentsSaga = withErrorHandling(
  function* _setChatSuggestionsConsentsSaga(
    action: GetActionByType<typeof SET_CHAT_SUGGESTIONS_CONSENTS>
  ) {
    const payload = {
      consents: [
        {
          code: 'chat_suggestion',
          category_code: 'message',
        },
      ],
      consent_action: action.payload.consentAction,
    };

    const newConsentStatus =
      action.payload.consentAction === CONSENT_ACTION_ACCEPT
        ? CHAT_SUGGESTION_CONSENT_ACCEPTED
        : CHAT_SUGGESTION_CONSENT_NOT_ACCEPTED;
    const currentState: ChatSuggestionConsents = yield* select(getChatSuggestionConsents);
    const newState = produce(currentState, (draft) => {
      if (draft.length > 0) {
        draft[0].consentStatus = newConsentStatus;
      }
    });

    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    yield* call(
      yield* call(getServiceFn, CMP_SERVICE, 'updateUserMessagingConsents' as const),
      payload
    );
    yield* put(setChatSuggestionConsentsSuccess(newState));
  }
);

function* updateMessagingConsentsErrorHandler() {
  yield* put(closeModal());
  yield* put(
    openModal(MODAL_MESSAGING_CONSENT, {
      props: {
        forceOpen: true,
      },
    })
  );
}

function* updateMessagingConsentsSuccessSaga() {
  yield* put(fetchMeta());
  yield* take(FETCH_META_SUCCESS);
  yield* put(closeModal());
}

const updateMessagingConsentsSaga = withErrorHandling(
  function* _updateMessagingConsentsSaga(
    action: GetActionByType<typeof UPDATE_MESSAGING_CONSENTS>
  ) {
    const { consentCode } = action.payload;
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    const response = yield* call(
      yield* call(getServiceFn, CMP_SERVICE, 'updateUserMessagingConsents' as const),
      {
        consents: [
          {
            code: consentCode,
          },
        ],
      }
    );
    const payload = processResponse(response);
    yield* put(updateMessagingConsentsSuccess(payload));
  },
  {
    name: 'updateMessagingConsentsErrorHandler',
    errorHandler: updateMessagingConsentsErrorHandler,
  }
);

function* updateBiometricConsentsErrorHandler() {
  yield* put(updateBiometricConsentsError());
}

function* updateBiometricConsentsSuccessSaga() {
  yield* put(fetchMeta());
}

const updateBiometricConsentsSaga = withErrorHandling(
  function* _updateBiometricConsentsSaga(
    action: GetActionByType<typeof UPDATE_BIOMETRIC_CONSENTS>
  ) {
    const { consentCode } = action.payload;
    // @ts-expect-error - TDP-175341:GET-SERVICE-FN-MISMATCH-TYPE
    const response = yield* call(
      yield* call(getServiceFn, CMP_SERVICE, 'updateUserMessagingConsents' as const),
      {
        consents: [
          {
            code: consentCode,
          },
        ],
      }
    );
    const payload = processResponse(response);
    yield* put(updateBiometricConsentsSuccess(payload));
  },
  {
    name: 'updateBiometricConsentsErrorHandler',
    errorHandler: updateBiometricConsentsErrorHandler,
  }
);

// if authed and there's a deferred update, push to backend, otherwise fetch
function* syncConsentsSaga() {
  const isAuthed = yield* select(getIsUserLoggedIn);
  const hasDeferredUpdate = yield* select(getHasDeferredUpdate);

  if (isAuthed && hasDeferredUpdate) {
    yield* put(updateConsents());
    yield* take([UPDATE_CONSENTS_SUCCESS, UPDATE_CONSENTS_ERROR]);
  } else {
    yield* put(fetchConsents());
    yield* take([FETCH_CONSENTS_SUCCESS, FETCH_CONSENTS_ERROR]);
  }
}

function* fetchConsentsSaga() {
  const consentRequestBody = yield* call(formatConsentRequestBody);

  const isAuthed = yield* select(getIsUserLoggedIn);
  yield* put(fetchConsentsRequest());
  try {
    const response = yield* call(
      yield* call(
        getServiceFn,
        CMP_SERVICE,
        isAuthed ? ('getUserConsents' as const) : ('getGeoConsents' as const)
      ),
      consentRequestBody
    );

    const payload = yield* call(processResponse, response, consentsResponseSchema);
    yield* put(fetchConsentsSuccess(payload));
  } catch (err: any) {
    const { error: response } = err;
    const payload = yield* call(processResponse, response, consentsErrorResponseSchema);
    yield* put(fetchConsentsError(payload));
  }
}

function* togglePrivacyPreferencesModal(action: GetActionByType<typeof PRIVACY_SETTINGS_MANAGE>) {
  let currentModal = yield* select(selectCurrentModal);
  if (action.payload.open) {
    // Kill any modals that are open
    while (currentModal) {
      // If we're already looking at privacy preferences, bail!
      if (currentModal.type === MODAL_PRIVACY_PREFERENCES) {
        return;
      }
      yield* put(closeModal());
      currentModal = yield* select(selectCurrentModal);
    }
    yield* put(openModal(MODAL_PRIVACY_PREFERENCES));
  } else if (currentModal?.type === MODAL_PRIVACY_PREFERENCES) {
    yield* put(closeModal());
  }
}

export const privacyRootSaga = withErrorHandling(
  function* _privacyRootSaga() {
    debug('privacyRootSaga');

    // Listen for CMP actions
    yield* takeEvery(FETCH_CONSENTS, fetchConsentsSaga);

    // Listen for actions that toggle the modal
    yield* takeEvery(PRIVACY_SETTINGS_MANAGE, togglePrivacyPreferencesModal);

    // Listen for privacy preference toggle actions
    yield* takeEvery(UPDATE_CONSENTS, updateConsentsSaga);

    yield* takeEvery(SET_CHAT_SUGGESTIONS_CONSENTS, setChatSuggestionsConsentsSaga);

    yield* takeEvery(UPDATE_MESSAGING_CONSENTS, updateMessagingConsentsSaga);
    yield* takeEvery(UPDATE_MESSAGING_CONSENTS_SUCCESS, updateMessagingConsentsSuccessSaga);

    yield* takeEvery(UPDATE_BIOMETRIC_CONSENTS, updateBiometricConsentsSaga);
    yield* takeEvery(UPDATE_BIOMETRIC_CONSENTS_SUCCESS, updateBiometricConsentsSuccessSaga);

    // Determine whether we need to show the privacy prompt after rehydration
    yield* call(getRehydrateLifecycle(STORE_PRIVACY).block);

    if (!__STATIC_SITE__) {
      // Wait until we know the user's auth state
      yield* call(initialAuthCheckComplete.block);
    }

    yield* call(syncConsentsSaga);

    // Prompt the user if we need any consents
    const needsPrivacyPrompt = yield* select(getNeedsPrivacyPrompt);
    if (needsPrivacyPrompt) {
      debug('show privacy prompt');
      yield* put(privacySettingsPrompt());
      yield* take(UPDATE_CONSENTS);
    } else {
      // Consents are already available
      const consents = yield* select(getPrivacyConsents);
      eventEmitter.emit(PRIVACY_SETTINGS_CONFIRMED, consents);
    }

    // The user has expressed their privacy preferences
    yield* call(privacyPreferencesSet.trigger);

    // Listen for login to send any pre-auth consents or sync with backend consents
    yield* takeEvery(LOGIN_SUCCESS, syncConsentsSaga);
  },
  { name: 'privacyRootSaga' }
);
